import type { VoidFunctionComponent } from 'react';

import { selectOptionFactory } from '../../../../utils';
import { FormGroup, FormLabelVariant, LabeledMultiTextInput, LabeledSelect } from '../../../generic/Forms';
import Link from '../../../generic/Link';
import ReadOnlySection from '../../../generic/ReadOnlySection';
import Redirect from '../../../generic/Redirect';
import JiraCustomFields from '../../../modules/jira/CustomFields';
import Header from '../../../shared/settings/SettingsPageHeader';
import Paragraph from '../../../shared/settings/SettingsPageParagraph';
import Subheading from '../../../shared/settings/SettingsPageSubheading';
import PageContent from '../shared/SettingsPageContent';
import type { JiraCustomField } from '../../../../../shared/jira';

import SiteConfigurationSection from './SiteConfigurationSection';
import UseGlobalTrackerSettingsRadios from './UseGlobalTrackerSettingsRadios';
import useProjectIssueTrackerSettings from './useProjectIssueTrackerSettings';

/**
 * ProjectIssueTrackerSettingsContainer
 */
const ProjectIssueTrackerSettingsContainer: VoidFunctionComponent = () => {
  const {
    jiras,
    settings,
    isLoading,
    isSaving,
    isSaveButtonDisabled,
    onSave,
    onUpdate,
    selectedJira,
    selectedJiraOption,
    onSelectedJiraChange,
    errors,
    canUserEditSettings,
    projectSettingsBaseURL,
  } = useProjectIssueTrackerSettings();

  if (!canUserEditSettings) {
    return (
      <Redirect to={projectSettingsBaseURL} />
    );
  }

  return (
    <PageContent isLoading={isLoading}>
      <Header heading="Issue Tracker">
        <Header.SaveButton
          isLoading={isSaving}
          isDisabled={isSaveButtonDisabled}
          onClick={onSave}
        />
        <Header.Paragraph>
          Project-specific configuration of the your org-level <Link to="/account/settings/integrations/jira">Jira Issue Settings</Link>.
        </Header.Paragraph>
        <Header.Errors errors={errors} />
      </Header>
      <UseGlobalTrackerSettingsRadios
        useGlobalTrackerSettings={settings.useGlobalTrackerSettings}
        isDisabled={isSaving}
        onUpdate={onUpdate}
      />
      <ReadOnlySection isReadOnly={!settings.useGlobalTrackerSettings}>
        <Subheading>
          Existing Jira Site
        </Subheading>
        <Paragraph size="small">
          When exporting a ticket to Jira, you will be able to select a globally configured Jira site to export to.
        </Paragraph>
        <LabeledSelect
          label="Select a Jira site to view its settings"
          variant={FormLabelVariant.Filter}
          id="project-settings-issue-tracker-site"
          value={selectedJiraOption}
          options={jiras.map(jira => selectOptionFactory(jira.id, jira.name))}
          onChange={onSelectedJiraChange}
          isDisabled={isLoading || isSaving}
        />
        {selectedJira && (
          <SiteConfigurationSection jira={selectedJira} />
        )}
      </ReadOnlySection>
      <ReadOnlySection isReadOnly={settings.useGlobalTrackerSettings}>
        <Subheading>
          Custom Jira Settings
        </Subheading>
        <Paragraph size="small">
          When exporting using custom site settings, you&apos;ll be able to
          {' '}
          export issues using the following settings for this Jira site.
        </Paragraph>
        <FormGroup>
          <LabeledMultiTextInput
            id="project-settings-issue-tracker-project-ids"
            label="Project ID"
            labelVariant={FormLabelVariant.Filter}
            placeholder="Type a project ID and hit Enter"
            values={settings.issueTrackerProjectIds}
            onChange={issueTrackerProjectIds => onUpdate({ issueTrackerProjectIds })}
            isDisabled={settings.useGlobalTrackerSettings || isSaving}
            size="small"
          />
          <LabeledMultiTextInput
            id="project-settings-issue-tracker-issue-types"
            label="Issue Types"
            labelVariant={FormLabelVariant.Filter}
            placeholder="Type an issue type and hit Enter"
            values={settings.issueTrackerIssueTypes}
            onChange={issueTrackerIssueTypes => onUpdate({ issueTrackerIssueTypes })}
            isDisabled={settings.useGlobalTrackerSettings || isSaving}
            size="small"
          />
          <LabeledMultiTextInput
            id="project-settings-issue-tracker-labels"
            label="Labels"
            labelVariant={FormLabelVariant.Filter}
            placeholder="Type a label and hit Enter"
            values={settings.issueTrackerLabels}
            onChange={issueTrackerLabels => onUpdate({ issueTrackerLabels })}
            isDisabled={settings.useGlobalTrackerSettings || isSaving}
            size="small"
          />
        </FormGroup>
        <Subheading>
          Custom Fields
        </Subheading>
        <JiraCustomFields
          customFields={settings.issueTrackerCustomFields || {}}
          onChange={(fields: Record<string, JiraCustomField>) => onUpdate({ issueTrackerCustomFields: fields })}
          isDisabled={settings.useGlobalTrackerSettings || isSaving}
        />
      </ReadOnlySection>
    </PageContent>
  );
};

export default ProjectIssueTrackerSettingsContainer;
